<template>
  <div
    :class="{
      'accordion-item rounded-[0.625rem] border border-www-color-neutral-very-light bg-www-color-white px-9 py-7 last:mb-0': true,
    }"
  >
    <h4
      :class="{
        'accordion-header': true,
      }"
    >
      <button
        :id="buttonId"
        type="button"
        :class="{
          'accordion-button flex w-full cursor-pointer items-center justify-between p-0 text-left text-lg font-bold leading-[1.3125rem] text-www-color-neutral-medium-dark hover:text-www-color-primary-lighter': true,
          'text-www-color-primary-lighter': isOpen,
          'transition-border transition-colors duration-150 ease-in-out': true,
        }"
        :aria-expanded="isOpen"
        :aria-controls="contentId"
        @click="toggleAccordion(index)"
      >
        <span>
          {{ question }}
        </span>

        <PtcIcon
          name="ptc-collapsed"
          :class="{
            'rotate-90 text-2xl leading-none transition-transform duration-300 ease-in-out': true,
            'rotate-[-90deg]': isOpen,
          }"
          alt="Collapsed Icon"
        />
      </button>
    </h4>
    <Transition name="slide">
      <div
        v-show="isOpen"
        :id="contentId"
        ref="contentRef"
        :class="{
          'accordion-collapse': true,
        }"
        role="region"
        :aria-labelledby="buttonId"
      >
        <div
          :class="{
            'accordion-body pb-0 pt-4 text-base font-medium leading-5 text-www-color-neutral-medium': true,
          }"
        >
          <PortableText
            v-if="answer.insideLayoutPortableText"
            :portable-data="answer.insideLayoutPortableText"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { getCurrentInstance } from 'vue';
import PtcIcon from '~/components/Atoms/PtcIcon/PtcIcon.vue';
import PortableText from '~/components/Atoms/PortableText/PortableText.vue';
import type { FaqQuestion } from '~/sanity/types';

type ExtendedFaqQuestion = FaqQuestion & {
  isOpen: boolean;
  index: number;
  idBase?: string;
};

const props = defineProps<ExtendedFaqQuestion>();
const model = defineModel<number | null>({ default: 1 });
const contentRef = ref<HTMLElement | null>(null);
const contentHeight = ref('0px');
const accordionInstanceId = getCurrentInstance()?.uid;
const idPrefix = computed(() => {
  const stableIdBase = props.idBase?.trim();
  if (stableIdBase) {
    return `accordion-${stableIdBase}`;
  }

  return `accordion-${accordionInstanceId ?? 'item'}`;
});
const buttonId = computed(() => `${idPrefix.value}-button-${props.index}`);
const contentId = computed(() => `${idPrefix.value}-content-${props.index}`);

const toggleAccordion = async (index: number) => {
  model.value = index === model.value ? null : index;
  await nextTick(); // Wait for DOM update

  if (props.isOpen && contentRef.value) {
    contentHeight.value = `${contentRef.value.scrollHeight}px`;
  } else {
    contentHeight.value = '0px';
  }
};
</script>

<style scoped>
.slide-enter-to,
.slide-leave-from {
  max-height: v-bind(contentHeight); /* Adjust based on your content */
  overflow: hidden;
}
</style>
